import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  exportPredefinedReport,
  fetchBranchOptions,
  fetchCompanyOptions,
  fetchDepartmentOptions,
  fetchPredefinedReports,
  fetchUpcomingReportSchedules,
  fetchProjectOptions,
  fetchReportModules,
  fetchReportPreferences,
  fetchUserOptions,
  runPredefinedReport,
  saveReportPreferences,
} from './api/reportsApi'
import { resolveMediaUrl } from '../../utils/mediaUrl'
import { parseStoredUser } from '../../utils/authSession'
import AppActionMenu from '../../components/AppActionMenu'
import AppDateInput from '../../components/AppDateInput'
import AppDropdown from '../../components/AppDropdown'
import PredefinedReportsDrawer from '../../components/PredefinedReportsDrawer'
import ReportColumnsModal from '../../components/ReportColumnsModal'
import ReportScheduleModal from './components/ReportScheduleModal'
import {
  fetchCustomReports,
  duplicateCustomReport,
  deleteCustomReport,
  exportCustomReport,
  exportCustomReportConfig,
} from './api/customReportsApi'
import CustomReportBuilderModal from './custom-builder/CustomReportBuilderModal'
import CustomReportViewModal from './custom-builder/components/CustomReportViewModal'
import CustomReportShareModal from './custom-builder/components/CustomReportShareModal'
import { appToast } from '../../components/AppToast'
import { appConfirm, appPrompt } from '../../components/AppConfirmModal'
import { normalizeReportError } from './utils/reportErrorUtils'
import useBodyScrollLock from '../../hooks/useBodyScrollLock'
import './ReportsScreen.css'

const MODULE_LABELS = {
  masters: 'Masters',
  financial: 'Financial',
  productivity: 'Productivity',
  task: 'Task',
  integration: 'Integration',
  project: 'Project',
  user: 'User',
  'api-logs': 'API Logs',
}

const HOME_CARD_CONFIG = [
  {
    moduleId: 'financial',
    icon: 'payments',
    title: 'Financial',
    description: 'Revenue, collection, and budget monitoring from live project data.',
    badge: 'Priority',
  },
  {
    moduleId: 'productivity',
    icon: 'speed',
    title: 'Productivity',
    description: 'Session activity, idle behavior, and monitoring outcomes.',
  },
  {
    moduleId: 'task',
    icon: 'task_alt',
    title: 'Task',
    description: 'Task backlog, priority movement, and completion posture.',
  },
  {
    moduleId: 'integration',
    icon: 'api',
    title: 'Integration',
    description: 'Ticket sync health and external integration outcomes.',
  },
  {
    moduleId: 'project',
    icon: 'account_tree',
    title: 'Project Intelligence',
    description: 'Timeline slippage and resource allocation across active projects.',
    wide: true,
  },
  {
    moduleId: 'user',
    icon: 'work',
    title: 'Job',
    description: 'Role/manager coverage and monitored workforce visibility.',
  },
  {
    moduleId: 'api-logs',
    icon: 'chat',
    title: 'Communication',
    description: 'Audit/auth event visibility for operational traceability.',
  },
]

const STATUS_OPTIONS = [
  'ACTIVE',
  'IN_PROGRESS',
  'DRAFT',
  'COMPLETED',
  'CLOSED',
  'FAILED',
  'SUCCESS',
  'PENDING',
]

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function toCsv(values) {
  return (values || []).filter(Boolean).join(',')
}

function normalizePreferenceColumns(columns) {
  if (!Array.isArray(columns)) return []
  return columns.map((column) => ({
    key: String(column?.key || ''),
    label: String(column?.label || column?.key || ''),
    visible: column?.visible !== false,
  }))
}

export default function ReportsScreen() {
  const navigate = useNavigate()
  const { module: routeModule = 'masters' } = useParams()
  const effectiveRouteModule = MODULE_LABELS[routeModule] ? routeModule : 'masters'

  const [selectedModule, setSelectedModule] = useState(effectiveRouteModule)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [columnSearch, setColumnSearch] = useState('')
  const [draggedColumn, setDraggedColumn] = useState(null)
  const [isLoadingModules, setIsLoadingModules] = useState(false)
  const [isLoadingReports, setIsLoadingReports] = useState(false)
  const [isRunningReport, setIsRunningReport] = useState(false)
  const [reportError, setReportError] = useState('')
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [schedulePrefill, setSchedulePrefill] = useState(null)
  const [upcomingSchedules, setUpcomingSchedules] = useState([])
  const [upcomingSchedulesLoading, setUpcomingSchedulesLoading] = useState(false)

  const [modules, setModules] = useState([])
  const [predefinedReports, setPredefinedReports] = useState([])
  const [selectedReportKey, setSelectedReportKey] = useState('')
  const [reportResult, setReportResult] = useState(null)
  // Track the settled module+reportKey to prevent stale-closure re-runs during module transitions
  const activeReportRef = useRef({ module: '', reportKey: '' })
  const [savedColumns, setSavedColumns] = useState([])
  const [draftColumns, setDraftColumns] = useState([])
  const [moduleInsights, setModuleInsights] = useState({})
  const [projectAvatarUsers, setProjectAvatarUsers] = useState([])
  const [projectAvatarOverflow, setProjectAvatarOverflow] = useState(0)

  const [filterState, setFilterState] = useState({
    companyId: '',
    branchId: '',
    departmentId: '',
    userId: '',
    projectId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  })
  const [companyOptions, setCompanyOptions] = useState([])
  const [branchOptions, setBranchOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [projectOptions, setProjectOptions] = useState([])

  const [activeTab, setActiveTab] = useState('predefined')
  const [customReports, setCustomReports] = useState([])
  const [customReportsLoading, setCustomReportsLoading] = useState(false)
  const [customBuilderOpen, setCustomBuilderOpen] = useState(false)
  const [editingCustomReportId, setEditingCustomReportId] = useState(null)
  const [viewingCustomReportId, setViewingCustomReportId] = useState(null)
  const [sharingCustomReport, setSharingCustomReport] = useState(null)

  useBodyScrollLock(
    drawerOpen ||
    settingsOpen ||
    scheduleModalOpen ||
    customBuilderOpen ||
    Boolean(viewingCustomReportId) ||
    Boolean(sharingCustomReport)
  )

  useEffect(() => {
    setSelectedModule(effectiveRouteModule)
  }, [effectiveRouteModule])

  const selectedModuleLabel = MODULE_LABELS[selectedModule] || MODULE_LABELS.masters
  const selectedReport = useMemo(
    () => predefinedReports.find((item) => item.reportKey === selectedReportKey) || predefinedReports[0] || null,
    [predefinedReports, selectedReportKey],
  )

  const companyDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Companies' }, ...companyOptions.map((item) => ({ value: String(item.id), label: item.name }))],
    [companyOptions],
  )
  const branchDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Branches' }, ...branchOptions.map((item) => ({ value: String(item.id), label: item.name }))],
    [branchOptions],
  )
  const departmentDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Departments' }, ...departmentOptions.map((item) => ({ value: String(item.id), label: item.name }))],
    [departmentOptions],
  )
  const userDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Users' }, ...userOptions.map((item) => ({ value: String(item.id), label: item.name }))],
    [userOptions],
  )
  const projectDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Projects' }, ...projectOptions.map((item) => ({ value: String(item.id), label: item.name }))],
    [projectOptions],
  )
  const statusDropdownOptions = useMemo(
    () => [{ value: '', label: 'All Status' }, ...STATUS_OPTIONS.map((status) => ({ value: status, label: status.replaceAll('_', ' ') }))],
    [],
  )

  const visibleColumns = useMemo(() => {
    const items = (settingsOpen ? draftColumns : savedColumns) || []
    return items.filter((item) => item.visible)
  }, [settingsOpen, draftColumns, savedColumns])

  const filteredDraftColumns = useMemo(() => {
    const text = columnSearch.trim().toLowerCase()
    if (!text) return draftColumns
    return draftColumns.filter(
      (column) =>
        column.key.toLowerCase().includes(text) ||
        (column.label || '').toLowerCase().includes(text),
    )
  }, [columnSearch, draftColumns])

  const appliedFiltersPayload = useMemo(() => {
    const payload = {
      companyIds: filterState.companyId ? [Number(filterState.companyId)] : [],
      branchIds: filterState.branchId ? [Number(filterState.branchId)] : [],
      departmentIds: filterState.departmentId ? [Number(filterState.departmentId)] : [],
      userIds: filterState.userId ? [Number(filterState.userId)] : [],
      projectIds: filterState.projectId ? [Number(filterState.projectId)] : [],
      status: filterState.status ? [filterState.status] : [],
      dateFrom: filterState.dateFrom || null,
      dateTo: filterState.dateTo || null,
      search: '',
      page: 1,
      pageSize: 25,
    }
    return payload
  }, [filterState])

  async function loadInitialData() {
    setIsLoadingModules(true)
    try {
      const [moduleRows, companies, users, projects] = await Promise.all([
        fetchReportModules(),
        fetchCompanyOptions({ page: 1, page_size: 200 }),
        fetchUserOptions({ page: 1, page_size: 200 }),
        fetchProjectOptions({ page: 1, page_size: 200 }),
      ])
      setModules(moduleRows)
      setCompanyOptions(companies)
      setUserOptions(users)
      setProjectOptions(projects)
    } catch (error) {
      setReportError(error?.response?.data?.detail?.message || 'Failed to load report modules.')
    } finally {
      setIsLoadingModules(false)
    }
  }

  async function loadUpcomingSchedules() {
    setUpcomingSchedulesLoading(true)
    try {
      const rows = await fetchUpcomingReportSchedules({ limit: 5 })
      setUpcomingSchedules(Array.isArray(rows) ? rows : [])
    } catch {
      setUpcomingSchedules([])
    } finally {
      setUpcomingSchedulesLoading(false)
    }
  }

  async function loadBranchesAndDepartments(companyId, branchId) {
    try {
      const params = {
        page: 1,
        page_size: 200,
      }
      if (companyId) params.company_ids = toCsv([companyId])
      const [branches, departments] = await Promise.all([
        fetchBranchOptions(params),
        fetchDepartmentOptions({
          ...params,
          branch_ids: branchId ? toCsv([branchId]) : undefined,
        }),
      ])
      setBranchOptions(branches)
      setDepartmentOptions(departments)
    } catch {
      setBranchOptions([])
      setDepartmentOptions([])
    }
  }

  async function runReportFor(moduleId, reportKey) {
    if (!moduleId || !reportKey) return null
    // Update the ref immediately so the filter effect knows what is "active"
    activeReportRef.current = { module: moduleId, reportKey }
    setIsRunningReport(true)
    setReportError('')
    try {
      const result = await runPredefinedReport({
        module: moduleId,
        reportKey,
        filters: appliedFiltersPayload,
      })
      setReportResult(result)
      const baseColumns = (result?.table?.columns || []).map((column) => ({
        key: column.key,
        label: column.label || column.key,
        visible: true,
      }))
      const preference = await fetchReportPreferences(moduleId, reportKey)
      const preferenceColumns = normalizePreferenceColumns(preference?.columns)
      const mergedColumns =
        preferenceColumns.length > 0
          ? preferenceColumns
              .map((saved) => {
                const base = baseColumns.find((column) => column.key === saved.key)
                if (!base) return null
                return {
                  ...base,
                  visible: saved.visible !== false,
                }
              })
              .filter(Boolean)
              .concat(baseColumns.filter((base) => !preferenceColumns.some((saved) => saved.key === base.key)))
          : baseColumns
      setSavedColumns(mergedColumns)
      setDraftColumns(mergedColumns)
      setModuleInsights((prev) => ({
        ...prev,
        [moduleId]: {
          reportKey,
          reportTitle: result?.reportTitle || '',
          totalRows: Number(result?.summary?.totalRows || 0),
          cards: Array.isArray(result?.cards) ? result.cards : [],
        },
      }))
      if (moduleId === 'project') {
        hydrateProjectAvatars(result?.table?.rows || [])
      }
      return result
    } catch (error) {
      setReportResult(null)
      setSavedColumns([])
      setDraftColumns([])
      setReportError(error?.response?.data?.detail?.message || 'Failed to run selected report.')
      return null
    } finally {
      setIsRunningReport(false)
    }
  }

  function hydrateProjectAvatars(rows) {
    const managerNames = Array.from(
      new Set((rows || []).map((row) => String(row?.manager_name || '').trim()).filter(Boolean)),
    )
    const resolved = managerNames.slice(0, 3).map((name) => {
      const user = userOptions.find((item) => String(item?.name || '').trim().toLowerCase() === name.toLowerCase())
      return {
        name,
        avatarUrl: resolveMediaUrl(user?.profile_photo_path),
      }
    })
    setProjectAvatarUsers(resolved)
    setProjectAvatarOverflow(Math.max(0, managerNames.length - resolved.length))
  }

  async function openModule(moduleId) {
    // Reset the active ref so the filter effect doesn't fire with stale data
    activeReportRef.current = { module: moduleId, reportKey: '' }
    setSelectedModule(moduleId)
    navigate(`/reports/${moduleId}`)
    setDrawerOpen(true)
    setReportError('')
    setSelectedReportKey('')
    setReportResult(null)
    setPredefinedReports([])
    setIsLoadingReports(true)
    try {
      const reports = await fetchPredefinedReports(moduleId)
      setPredefinedReports(reports)
      const first = reports[0]
      if (first?.reportKey) {
        setSelectedReportKey(first.reportKey)
        await runReportFor(moduleId, first.reportKey)
      } else {
        activeReportRef.current = { module: moduleId, reportKey: '' }
        setReportResult(null)
      }
    } catch (error) {
      activeReportRef.current = { module: moduleId, reportKey: '' }
      setPredefinedReports([])
      setSelectedReportKey('')
      setReportResult(null)
      setReportError(error?.response?.data?.detail?.message || 'Failed to load predefined reports.')
    } finally {
      setIsLoadingReports(false)
    }
  }

  async function handleSelectReport(reportKey) {
    setSelectedReportKey(reportKey)
    await runReportFor(selectedModule, reportKey)
  }

  function onFilterChange(field, value) {
    setFilterState((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'companyId') {
        next.branchId = ''
        next.departmentId = ''
      }
      if (field === 'branchId') {
        next.departmentId = ''
      }
      return next
    })
  }

  function createPrintWindow() {
    const printWindow = window.open('about:blank', '_blank', 'width=1200,height=850')
    if (!printWindow) return null
    printWindow.document.open()
    printWindow.document.write(
      '<!doctype html><html><head><title>Preparing report...</title></head><body style="font-family:Arial,sans-serif;padding:24px;">Preparing print preview...</body></html>',
    )
    printWindow.document.close()
    return printWindow
  }

  function triggerBrowserPrint(printWindow, printData, columnsForPrint) {
    if (!printWindow || !printData?.printPayload) return
    const columns = columnsForPrint?.length > 0 ? columnsForPrint : printData.printPayload.columns || []
    const rows = printData?.printPayload?.rows || []

    const header = columns.map((column) => `<th>${escapeHtml(column.label || column.key)}</th>`).join('')
    const body = rows
      .map((row) => {
        const cells = columns
          .map((column) => `<td>${escapeHtml(row?.[column.key] ?? '')}</td>`)
          .join('')
        return `<tr>${cells}</tr>`
      })
      .join('')
    const generatedAt = new Date().toLocaleString()

    printWindow.document.open()
    printWindow.document.write(
      `<!doctype html><html><head><meta charset="utf-8" /><title>${escapeHtml(printData.reportTitle || 'Report')}</title><style>
      body{font-family:Arial,sans-serif;margin:24px;color:#191c1e}
      h1{margin:0 0 6px;font-size:24px;color:#00327d}
      p{margin:0 0 16px;color:#434653;font-size:12px}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #d7dce6;padding:8px;text-align:left;font-size:12px;vertical-align:top}
      th{background:#f2f4f6;font-size:11px;text-transform:uppercase;letter-spacing:.05em}
      </style></head><body>
      <h1>${escapeHtml(printData.reportTitle || 'Report')}</h1>
      <p>Generated on ${escapeHtml(generatedAt)}</p>
      <table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>
      <script>window.onload=()=>window.print();</script>
      </body></html>`,
    )
    printWindow.document.close()
  }

  async function triggerExcelExport(moduleId, reportKey) {
    if (!moduleId || !reportKey) return
    try {
      const { blob, filename } = await exportPredefinedReport({
        module: moduleId,
        reportKey,
        format: 'xls',
        filters: appliedFiltersPayload,
      })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      setReportError(error?.response?.data?.detail?.message || 'Failed to export report.')
    }
  }

  const loadCustomReports = async (scope = 'all') => {
    setCustomReportsLoading(true)
    try {
      const rows = await fetchCustomReports({ scope })
      setCustomReports(rows)
    } catch (err) {
      console.error('Failed to load custom reports:', err)
    } finally {
      setCustomReportsLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'predefined') {
      loadCustomReports(activeTab)
    }
  }, [activeTab])

  async function handleDuplicateCustomReport(report) {
    const newName = await appPrompt({
      title: 'Duplicate Report',
      message: 'Enter name for duplicate report:',
      defaultValue: `${report.reportName} (Copy)`,
      confirmText: 'Duplicate',
    })
    if (!newName) return
    try {
      await duplicateCustomReport(report.id, newName)
      appToast.success('Report duplicated successfully.')
      loadCustomReports(activeTab)
    } catch (err) {
      appToast.error(normalizeReportError(err, 'Failed to duplicate report'))
    }
  }

  async function handleDeleteCustomReport(report) {
    const confirmed = await appConfirm({
      title: 'Delete Report',
      message: `Are you sure you want to delete "${report.reportName}"?`,
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!confirmed) return
    try {
      await deleteCustomReport(report.id)
      appToast.success('Report deleted successfully.')
      loadCustomReports(activeTab)
    } catch (err) {
      appToast.error(normalizeReportError(err, 'Failed to delete report'))
    }
  }

  async function handleExportCustomReport(report, format = 'xlsx') {
    try {
      const { blob, filename } = await exportCustomReport(report.id, format)
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      appToast.error(normalizeReportError(err, 'Export failed'))
    }
  }

  async function onAction(action, reportKey, reportObj) {
    setSelectedReportKey(reportKey)
    if (action === 'schedule') {
      const targetReport = reportObj || predefinedReports.find((item) => item.reportKey === reportKey)
      setSchedulePrefill({
        module: selectedModule,
        reportKey,
        reportTitle: targetReport?.title || reportKey,
        filters: appliedFiltersPayload,
      })
      setScheduleModalOpen(true)
      return
    }
    if (action === 'print') {
      const printWindow = createPrintWindow()
      if (!printWindow) {
        setReportError('Print preview popup was blocked. Please allow popups for this site and retry.')
        return
      }
      let resultForPrint = reportResult
      let columnsForPrint = visibleColumns
      if (reportKey !== selectedReport?.reportKey || !resultForPrint) {
        resultForPrint = await runReportFor(selectedModule, reportKey)
        columnsForPrint = (resultForPrint?.table?.columns || []).map((column) => ({
          key: column.key,
          label: column.label || column.key,
          visible: true,
        }))
      }
      if (!resultForPrint?.printPayload) {
        printWindow.close()
        setReportError('No printable data found for this report.')
        return
      }
      triggerBrowserPrint(printWindow, resultForPrint, columnsForPrint)
      return
    }
    if (reportKey !== selectedReport?.reportKey) {
      await runReportFor(selectedModule, reportKey)
    }
    if (action === 'excel') {
      await triggerExcelExport(selectedModule, reportKey)
      return
    }
    if (action === 'setting') {
      setColumnSearch('')
      setDraftColumns(savedColumns)
      setSettingsOpen(true)
    }
  }

  function moveDraftColumn(fromKey, toKey) {
    if (!fromKey || !toKey || fromKey === toKey) return
    const fromIndex = draftColumns.findIndex((item) => item.key === fromKey)
    const toIndex = draftColumns.findIndex((item) => item.key === toKey)
    if (fromIndex < 0 || toIndex < 0) return
    const next = [...draftColumns]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    setDraftColumns(next)
  }

  async function applyColumnSettings(customColumns) {
    const colsToSave = Array.isArray(customColumns) ? customColumns : draftColumns
    const payloadColumns = colsToSave.map((column) => ({
      key: column.key,
      label: column.label || column.key,
      visible: column.visible !== false,
    }))
    try {
      await saveReportPreferences({
        module: selectedModule,
        reportKey: selectedReport?.reportKey,
        columns: payloadColumns,
      })
      setSavedColumns(payloadColumns)
      setSettingsOpen(false)
    } catch (error) {
      setReportError(error?.response?.data?.detail?.message || 'Failed to save column settings.')
    }
  }

  useEffect(() => {
    loadInitialData()
    loadUpcomingSchedules()
  }, [])

  useEffect(() => {
    function refreshUpcoming() {
      loadUpcomingSchedules()
    }
    window.addEventListener('report-schedules-updated', refreshUpcoming)
    return () => window.removeEventListener('report-schedules-updated', refreshUpcoming)
  }, [])

  useEffect(() => {
    loadBranchesAndDepartments(filterState.companyId, filterState.branchId)
  }, [filterState.companyId, filterState.branchId])

  useEffect(() => {
    // Only re-run if the drawer is open AND the active ref module+key matches what is currently shown
    // This prevents stale-closure mismatches when switching modules rapidly
    const { module: activeModule, reportKey: activeKey } = activeReportRef.current
    if (!drawerOpen || !activeModule || !activeKey) return
    if (activeModule !== selectedModule) return
    if (activeKey !== selectedReport?.reportKey) return
    runReportFor(activeModule, activeKey)
  }, [appliedFiltersPayload])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (projectAvatarUsers.length > 0) return
    ;(async () => {
      try {
        const reports = await fetchPredefinedReports('project')
        const first = reports[0]
        if (!first?.reportKey) return
        const result = await runPredefinedReport({
          module: 'project',
          reportKey: first.reportKey,
          filters: appliedFiltersPayload,
        })
        hydrateProjectAvatars(result?.table?.rows || [])
      } catch {
        // Keep avatar placeholders.
      }
    })()
  }, [userOptions])

  function moduleMeta(moduleId) {
    return modules.find((item) => item.id === moduleId) || null
  }

  return (
    <div className="reports-stitch bg-surface text-on-surface">
      <div className="px-8 py-10 max-w-7xl mx-auto w-full space-y-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="font-headline text-5xl font-extrabold text-on-surface tracking-tight mb-2">
              Global Reports Hub
            </h1>
            <p className="text-on-surface-variant text-lg max-w-xl">
              Centralized intelligence center for Bridge Enterprise using live report data.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setEditingCustomReportId(null)
                setCustomBuilderOpen(true)
              }}
              className="secondary-gradient bg-secondary-container text-on-secondary-container px-6 py-3 rounded-md font-semibold text-sm hover:opacity-90 transition-opacity flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">add_circle</span>
              Create Custom Report
            </button>
            <button
              type="button"
              onClick={() => {
                triggerExcelExport(selectedModule, predefinedReports[0]?.reportKey || 'org-hierarchy-with-headcount')
              }}
              className="primary-gradient text-on-primary px-6 py-3 rounded-md font-semibold text-sm shadow-lg shadow-primary/20 hover:opacity-90 transition-opacity flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">download</span>
              Quick Export
            </button>
          </div>
        </div>

        <section className="bg-surface-container-low p-6 rounded-lg ghost-border">
          <div className="flex items-center gap-2 mb-6">
            <span className="material-symbols-outlined text-primary">filter_list</span>
            <h2 className="font-label text-xs font-extrabold uppercase tracking-[0.1em] text-on-surface-variant">
              Global Intelligence Filters
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Company</label>
              <AppDropdown
                value={filterState.companyId}
                onChange={(value) => onFilterChange('companyId', String(value))}
                options={companyDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Branch</label>
              <AppDropdown
                value={filterState.branchId}
                onChange={(value) => onFilterChange('branchId', String(value))}
                options={branchDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Department</label>
              <AppDropdown
                value={filterState.departmentId}
                onChange={(value) => onFilterChange('departmentId', String(value))}
                options={departmentDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">User</label>
              <AppDropdown
                value={filterState.userId}
                onChange={(value) => onFilterChange('userId', String(value))}
                options={userDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Project</label>
              <AppDropdown
                value={filterState.projectId}
                onChange={(value) => onFilterChange('projectId', String(value))}
                options={projectDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <AppDateInput
                  value={filterState.dateFrom}
                  onChange={(value) => onFilterChange('dateFrom', value)}
                  inputClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
                />
                <AppDateInput
                  value={filterState.dateTo}
                  onChange={(value) => onFilterChange('dateTo', value)}
                  inputClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-bold text-on-surface-variant uppercase px-1">Status</label>
              <AppDropdown
                value={filterState.status}
                onChange={(value) => onFilterChange('status', String(value))}
                options={statusDropdownOptions}
                triggerClassName="w-full bg-surface-container-lowest border-none text-xs rounded-md shadow-none"
              />
            </div>
          </div>
        </section>

        {reportError ? (
          <div className="bg-error-container text-on-error-container rounded-md px-4 py-3 text-sm">
            {reportError}
          </div>
        ) : null}

        {/* ── Report Catalog vs Custom Reports Tab Switcher ── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-surface-container pb-3">
          <button
            type="button"
            onClick={() => setActiveTab('predefined')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
              activeTab === 'predefined'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-sm">grid_view</span>
            <span>Predefined Catalog</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('my')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
              activeTab === 'my'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-sm">person</span>
            <span>My Reports</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('shared')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
              activeTab === 'shared'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-sm">group</span>
            <span>Shared with Me</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs transition-colors ${
              activeTab === 'all'
                ? 'bg-primary text-on-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-sm">auto_graph</span>
            <span>All Custom Reports</span>
          </button>
        </div>

        {activeTab === 'predefined' ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <button
            type="button"
            onClick={() => openModule('masters')}
            className="md:col-span-2 md:row-span-2 bg-surface-container-lowest rounded-lg p-8 ghost-border group hover:bg-slate-50 transition-all flex flex-col text-left"
          >
            <div className="w-14 h-14 bg-primary-fixed flex items-center justify-center rounded-lg mb-6 group-hover:scale-110 transition-transform">
              <span className="material-symbols-outlined text-primary text-3xl">hub</span>
            </div>
            <h3 className="font-headline text-2xl font-extrabold mb-3">Master Reports</h3>
            <p className="text-on-surface-variant text-sm mb-6">
              Unified report index backed by predefined catalog and access-bound live data.
            </p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center justify-between">
                <span className="text-sm font-medium text-on-surface">
                  Available Modules: {modules.filter((item) => item.available).length}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-sm font-medium text-on-surface">
                  Predefined Reports: {modules.reduce((sum, item) => sum + Number(item.reportCount || 0), 0)}
                </span>
              </li>
            </ul>
            <div className="mt-auto">
              <div className="bg-surface-container-low rounded-full h-2 w-full mb-2">
                <div
                  className="bg-primary h-full rounded-full"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(
                        ((modules.filter((item) => item.available).length || 0) /
                          (modules.length || 1)) *
                          100,
                      ),
                    )}%`,
                  }}
                ></div>
              </div>
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                Access Coverage: {modules.filter((item) => item.available).length}/{modules.length || 0} modules
              </span>
            </div>
          </button>

          {HOME_CARD_CONFIG.filter((card) => {
            const activeModules = parseStoredUser().modules || []
            if (card.moduleId === 'productivity') return activeModules.includes('bruno')
            if (['financial', 'project', 'task', 'integration', 'user'].includes(card.moduleId)) return activeModules.includes('nerve')
            if (card.moduleId === 'api-logs') return activeModules.includes('bridge')
            return true
          }).map((card) => {
            const meta = moduleMeta(card.moduleId)
            const insight = moduleInsights[card.moduleId]
            const isProjectCard = card.moduleId === 'project'
            return (
              <button
                key={card.moduleId}
                type="button"
                onClick={() => openModule(card.moduleId)}
                className={`${card.wide ? 'md:col-span-2' : ''} bg-surface-container-lowest rounded-lg p-6 ghost-border group hover:shadow-xl transition-all text-left`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="material-symbols-outlined text-primary">{card.icon}</span>
                  {card.badge ? (
                    <span className="text-[10px] font-bold bg-error-container text-on-error-container px-2 py-1 rounded">
                      {card.badge}
                    </span>
                  ) : null}
                </div>
                <h4 className="font-headline text-lg font-extrabold mb-1">{card.title}</h4>
                <p className="text-xs text-on-surface-variant mb-3">{card.description}</p>
                <p className="text-[11px] text-on-surface-variant font-semibold">
                  {meta
                    ? `${meta.reportCount || 0} predefined reports`
                    : isLoadingModules
                      ? 'Loading module catalog...'
                      : 'Module metadata unavailable'}
                </p>
                {insight ? (
                  <p className="text-[11px] text-primary font-semibold mt-1">
                    {insight.reportTitle}: {insight.totalRows} rows
                  </p>
                ) : null}

                {isProjectCard ? (
                  <div className="flex -space-x-2 mt-4">
                    {projectAvatarUsers.length > 0
                      ? projectAvatarUsers.map((user) =>
                          user.avatarUrl ? (
                            <img
                              key={user.name}
                              src={user.avatarUrl}
                              alt={user.name}
                              title={user.name}
                              className="w-8 h-8 rounded-full border-2 border-white object-cover"
                            />
                          ) : (
                            <div
                              key={user.name}
                              title={user.name}
                              className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] font-bold"
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          ),
                        )
                      : [0, 1].map((index) => (
                          <div
                            key={index}
                            className="w-8 h-8 rounded-full border-2 border-white bg-slate-200"
                          ></div>
                        ))}
                    {projectAvatarOverflow > 0 ? (
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-surface-container-high flex items-center justify-center text-[10px] font-bold">
                        +{projectAvatarOverflow}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </button>
            )
          })}
        </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold font-headline text-on-surface">
                  {activeTab === 'my'
                    ? 'My Custom Reports'
                    : activeTab === 'shared'
                    ? 'Reports Shared with Me'
                    : 'All Accessible Custom Reports'}
                </h3>
                <p className="text-xs text-on-surface-variant">
                  Tailored management & operational reports created using the No-Code Report Builder.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingCustomReportId(null)
                  setCustomBuilderOpen(true)
                }}
                className="primary-gradient text-on-primary px-4 py-2 rounded-lg font-semibold text-xs shadow hover:opacity-90 transition-opacity flex items-center gap-1.5 self-start md:self-auto"
              >
                <span className="material-symbols-outlined text-sm">add</span>
                Build New Report
              </button>
            </div>

            {customReportsLoading ? (
              <div className="flex flex-col items-center justify-center py-16 bg-surface-container-low rounded-xl border border-surface-container space-y-3">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs font-semibold text-on-surface-variant">Loading custom reports...</p>
              </div>
            ) : customReports.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 bg-surface-container-low rounded-xl border border-surface-container text-center p-6 space-y-3">
                <span className="material-symbols-outlined text-4xl text-slate-300">description</span>
                <div>
                  <h4 className="text-sm font-bold text-on-surface">No custom reports found</h4>
                  <p className="text-xs text-on-surface-variant max-w-sm mt-1">
                    {activeTab === 'my'
                      ? "You haven't built any custom reports yet. Click 'Create Custom Report' to start building."
                      : "No reports have been shared under this scope."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setEditingCustomReportId(null)
                    setCustomBuilderOpen(true)
                  }}
                  className="px-4 py-2 bg-primary text-on-primary rounded-lg text-xs font-bold"
                >
                  Create Custom Report
                </button>
              </div>
            ) : (
              <div className="bg-surface rounded-xl border border-surface-container overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-surface-container-low border-b border-surface-container">
                      <tr>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Report Name
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Module / Category
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Owner
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Created
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Last Run
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider">
                          Visibility
                        </th>
                        <th className="py-3 px-4 font-extrabold text-[10px] uppercase text-on-surface-variant tracking-wider text-right">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-container-low">
                      {customReports.map((report) => (
                        <tr key={report.id} className="hover:bg-surface-container-lowest transition-colors">
                          <td className="py-3 px-4">
                            <button
                              type="button"
                              onClick={() => setViewingCustomReportId(report.id)}
                              className="font-bold text-primary hover:underline text-left block"
                            >
                              {report.reportName}
                            </button>
                            {report.description && (
                              <p className="text-[11px] text-on-surface-variant truncate max-w-xs">{report.description}</p>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-[10px] font-bold uppercase bg-surface-container-high px-2 py-0.5 rounded text-on-surface-variant">
                              {report.primaryModule}
                            </span>
                            <span className="text-[11px] text-on-surface-variant ml-2">{report.category}</span>
                          </td>
                          <td className="py-3 px-4 text-on-surface font-medium">
                            {report.ownerName || 'Unknown'}
                          </td>
                          <td className="py-3 px-4 text-on-surface-variant text-[11px]">
                            {report.createdAt ? new Date(report.createdAt).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-3 px-4 text-on-surface-variant text-[11px]">
                            {report.lastRunAt ? new Date(report.lastRunAt).toLocaleDateString() : 'Never run'}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                              report.visibility === 'PRIVATE'
                                ? 'bg-slate-100 text-slate-700'
                                : report.visibility === 'ORG'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {report.visibility}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end">
                              <AppActionMenu
                                triggerLabel=""
                                triggerIcon="more_vert"
                                showChevron={false}
                                items={[
                                  { key: 'run', label: 'Run Report', icon: 'play_arrow' },
                                  { key: 'edit', label: 'Edit Builder', icon: 'edit' },
                                  { key: 'duplicate', label: 'Duplicate (Save As)', icon: 'content_copy' },
                                  { key: 'schedule', label: 'Schedule Dispatch', icon: 'schedule' },
                                  { key: 'share', label: 'Share Permissions', icon: 'share' },
                                  { key: 'export_xlsx', label: 'Excel (.xlsx)', icon: 'table_view' },
                                  { key: 'export_xls', label: 'Legacy Excel (.xls)', icon: 'table_view' },
                                  { key: 'export_csv', label: 'Export CSV', icon: 'description' },
                                  { key: 'export_pdf', label: 'Export PDF', icon: 'picture_as_pdf' },
                                  { key: 'delete', label: 'Delete Report', icon: 'delete', danger: true },
                                ]}
                                onSelect={(actionKey) => {
                                  if (actionKey === 'run') setViewingCustomReportId(report.id)
                                  else if (actionKey === 'edit') {
                                    setEditingCustomReportId(report.id)
                                    setCustomBuilderOpen(true)
                                  } else if (actionKey === 'duplicate') handleDuplicateCustomReport(report)
                                  else if (actionKey === 'schedule') {
                                    setSchedulePrefill({
                                      module: report.primaryModule,
                                      reportKey: `custom_${report.id}`,
                                      reportTitle: report.reportName,
                                    })
                                    setScheduleModalOpen(true)
                                  } else if (actionKey === 'share') setSharingCustomReport(report)
                                  else if (actionKey === 'export_xlsx') handleExportCustomReport(report, 'xlsx')
                                  else if (actionKey === 'export_xls') handleExportCustomReport(report, 'xls')
                                  else if (actionKey === 'export_csv') handleExportCustomReport(report, 'csv')
                                  else if (actionKey === 'export_pdf') handleExportCustomReport(report, 'pdf')
                                  else if (actionKey === 'delete') handleDeleteCustomReport(report)
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <section className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-headline text-xl font-extrabold">Next Scheduled Dispatches</h3>
            <button
              type="button"
              className="text-primary font-bold text-sm hover:underline underline-offset-4 transition-all"
              onClick={() => {
                navigate('/reports/schedules/list')
              }}
            >
              Manage Schedules
            </button>
          </div>
          <div className="space-y-3">
            {upcomingSchedulesLoading ? (
              <div className="bg-surface-container-low p-4 rounded-lg text-sm text-on-surface-variant">
                Loading scheduled dispatches...
              </div>
            ) : upcomingSchedules.length === 0 ? (
              <div className="bg-surface-container-low p-4 rounded-lg text-sm text-on-surface-variant">
                No upcoming dispatches configured.
              </div>
            ) : (
              upcomingSchedules.map((schedule) => {
                const runAt = schedule?.nextRunAt ? new Date(schedule.nextRunAt) : null
                const timeText = runAt ? runAt.toLocaleString() : 'Not scheduled'
                return (
                  <div key={schedule.id} className="bg-surface-container-low p-4 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded flex items-center justify-center">
                        <span className="material-symbols-outlined text-slate-400">calendar_today</span>
                      </div>
                      <div>
                        <h5 className="text-sm font-bold">{schedule.reportTitle || schedule.reportKey}</h5>
                        <p className="text-xs text-on-surface-variant">Recipients: {schedule.recipientCount || 0}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="block text-sm font-bold">{timeText}</span>
                      <span className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">
                        {String(schedule.recurrenceType || '').replaceAll('_', ' ')}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </section>
      </div>

      {/* ── Global Predefined Reports Drawer ── */}
      <PredefinedReportsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Predefined Reports"
        module={selectedModule}
        moduleLabel={selectedModuleLabel}
        subtitle={`${selectedModuleLabel} Analytics Module`}
        reports={predefinedReports}
        selectedReportKey={selectedReportKey}
        onSelectReport={handleSelectReport}
        onAction={onAction}
        isLoading={isLoadingReports}
        reportResult={reportResult}
      />

      <ReportScheduleModal
        open={scheduleModalOpen}
        prefill={schedulePrefill}
        module={selectedModule}
        moduleLabel={selectedModuleLabel}
        onClose={() => {
          setScheduleModalOpen(false)
          setSchedulePrefill(null)
        }}
        onChanged={() => {
          loadUpcomingSchedules()
        }}
      />

      {/* ── Global Report Columns Settings Modal ── */}
      <ReportColumnsModal
        open={settingsOpen}
        report={selectedReport}
        savedColumns={savedColumns}
        onClose={() => setSettingsOpen(false)}
        onSave={applyColumnSettings}
      />

      {/* ── Custom Reports Builder Modal ── */}
      <CustomReportBuilderModal
        open={customBuilderOpen}
        reportId={editingCustomReportId}
        initialPrefill={{
          companyId: filterState.companyId,
          branchId: filterState.branchId,
          departmentId: filterState.departmentId,
          projectId: filterState.projectId,
          filters: appliedFiltersPayload,
        }}
        onClose={() => {
          setCustomBuilderOpen(false)
          setEditingCustomReportId(null)
        }}
        onSaved={() => {
          loadCustomReports(activeTab)
        }}
        onTriggerSchedule={(info) => {
          setSchedulePrefill({
            module: info?.module || 'project',
            reportKey: editingCustomReportId ? `custom_${editingCustomReportId}` : 'custom_draft',
            reportTitle: info?.reportName || 'Custom Report',
            filters: info?.config?.filters || {},
          })
          setScheduleModalOpen(true)
        }}
        onTriggerExport={async (reportName, config, fmt) => {
          try {
            const { blob, filename } = await exportCustomReportConfig(reportName, config, fmt)
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
          } catch (err) {
            console.error('Export custom report config failed:', err)
            appToast.error('Export failed: ' + (err?.response?.data?.detail?.message || err?.message || 'Error'))
          }
        }}
        companyOptions={companyDropdownOptions}
        branchOptions={branchDropdownOptions}
        departmentOptions={departmentDropdownOptions}
        projectOptions={projectDropdownOptions}
      />

      {/* ── Custom Report Interactive Runner / Viewer Modal ── */}
      <CustomReportViewModal
        open={Boolean(viewingCustomReportId)}
        reportId={viewingCustomReportId}
        onClose={() => setViewingCustomReportId(null)}
        onEdit={(id) => {
          setEditingCustomReportId(id)
          setCustomBuilderOpen(true)
        }}
        onPrint={(result) => {
          triggerBrowserPrint(createPrintWindow(), result, result?.columns)
        }}
      />

      {/* ── Custom Report Share / Permissions Modal ── */}
      <CustomReportShareModal
        open={Boolean(sharingCustomReport)}
        report={sharingCustomReport}
        onClose={() => setSharingCustomReport(null)}
        onSaved={() => {
          loadCustomReports(activeTab)
        }}
      />
    </div>
  )
}
